// Los filtros de las bandejas sobreviven a salir de la pantalla y volver.
//
// EL DEFECTO (bug 628 · CP-11-02 p.10 del Comité, bug 683 · CP-50-01 p.12 de Asuntos
// Regulatorios; QA, 2.º ciclo del Sprint 2). Abrir un caso en solo lectura CAMBIA DE
// MÓDULO: `App` monta el expediente y la bandeja se desmonta entera. Como los filtros
// nacían con `useState(SIN_FILTROS)`, al volver la bandeja aparecía en blanco y había que
// rehacer el filtro país por país. Con 14 mercados en la misma cola, revisar diez casos
// filtrados significaba filtrar diez veces.
//
// Mismo patrón que `vistaPersistida.ts`, por las mismas razones:
//   · `sessionStorage`: muere con la pestaña y no cruza a otra;
//   · todo acceso en try/catch: en ventana privada el accesor LANZA al tocarlo;
//   · lo que se lee SE COMPRUEBA. De estos valores sale la query string que se manda al
//     servidor, y entre escribir y leer `sessionStorage` es editable desde la consola: solo
//     se aceptan las claves que la pantalla declara y solo si traen texto de largo razonable.
//
// SE OLVIDAN AL CERRAR SESIÓN (`App.alCerrarSesion`), como la vista y por lo mismo: un
// filtro del Comité que sobreviviera al cambio de usuario sería el contexto de una persona
// asomado en la pantalla de otra.

/** Una sola clave para las dos bandejas: cerrar sesión las olvida de un solo golpe. */
const CLAVE = 'fv_filtros';

/**
 * Lo más largo que se acepta en un filtro. Un nombre de producto o un número de caso no
 * pasan de aquí; lo que lo pase no lo tecleó nadie en esta pantalla.
 */
const TOPE = 120;

/**
 * Qué pantalla guarda: cada una tiene sus filtros y no se pisan. `general` es la Bandeja de
 * casos (HU-34), `indicadores` el tablero (HU-35, periodo y país) y `seguimiento` Disposiciones y
 * seguimiento (HU-27): vuelven con sus filtros al regresar de un expediente.
 */
export type ClaveDeBandeja = 'comite' | 'regulatorio' | 'seguimiento' | 'general' | 'indicadores';

/** La forma común de los filtros de una bandeja: claves conocidas con texto. */
export type FiltrosDeUnaBandeja = Record<string, string | undefined>;

/** Lo guardado, ya parseado. `{}` si no hay nada o si lo que hay no es un objeto. */
function leerGuardado(): Record<string, unknown> {
  try {
    const crudo = sessionStorage.getItem(CLAVE);
    if (!crudo) return {};
    const guardado: unknown = JSON.parse(crudo);
    return guardado && typeof guardado === 'object' ? (guardado as Record<string, unknown>) : {};
  } catch {
    // Sin almacenamiento —o con un JSON roto— se arranca sin filtros, que es lo de siempre.
    return {};
  }
}

/**
 * Los filtros con los que MONTA la bandeja: los guardados si valen, si no los vacíos.
 *
 * `sinFiltros` hace de lista blanca: solo se recuperan sus claves, así que nada que se
 * inyecte a mano en el almacenamiento llega al servidor como un filtro que la pantalla no
 * ofrece. El `as T` es seguro por eso mismo —las claves son las suyas y los valores, texto—.
 */
export function filtrosIniciales<T extends FiltrosDeUnaBandeja>(bandeja: ClaveDeBandeja, sinFiltros: T): T {
  const guardados = leerGuardado()[bandeja];
  if (!guardados || typeof guardados !== 'object') return sinFiltros;
  const recuperados: Record<string, string> = {};
  for (const clave of Object.keys(sinFiltros)) {
    const valor = (guardados as Record<string, unknown>)[clave];
    if (typeof valor === 'string' && valor.length > 0 && valor.length <= TOPE) recuperados[clave] = valor;
  }
  return { ...sinFiltros, ...recuperados } as T;
}

/** Guarda los filtros de UNA bandeja sin tocar los de la otra. */
export function guardarFiltros(bandeja: ClaveDeBandeja, filtros: FiltrosDeUnaBandeja): void {
  try {
    const guardado = leerGuardado();
    // Solo lo puesto: un filtro vacío no es un filtro, y guardarlo engordaría la clave
    // con siete cadenas en blanco por bandeja.
    const puestos: Record<string, string> = {};
    for (const [clave, valor] of Object.entries(filtros)) {
      if (typeof valor === 'string' && valor !== '') puestos[clave] = valor.slice(0, TOPE);
    }
    guardado[bandeja] = puestos;
    sessionStorage.setItem(CLAVE, JSON.stringify(guardado));
  } catch {
    // Sin almacenamiento los filtros no sobreviven al cambio de pantalla; no merece
    // romper la bandeja por eso.
  }
}

/** Cerrar sesión los olvida: los filtros son del usuario, no de la pestaña. */
export function olvidarFiltros(): void {
  try {
    sessionStorage.removeItem(CLAVE);
  } catch {
    /* nada que olvidar */
  }
}
